import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Footer() {
  const { t, localePath } = useI18n();

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0F172A] text-slate-200 border-t border-[#1E293B]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center h-10 w-10 rounded-md bg-white text-[#0F2942] font-bold">
                M
              </span>
              <div>
                <div className="text-white font-semibold text-lg">
                  {t("nav.brand")}
                </div>
                <div className="text-xs uppercase tracking-widest text-slate-400">
                  {t("nav.brand_sub")}
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-400 max-w-md leading-relaxed">
              {t("footer.brand_tagline")}
            </p>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 text-[#2A6B7C]" />
                <div>
                  <div className="text-slate-500 text-xs uppercase tracking-widest">
                    {t("footer.phone_label")}
                  </div>
                  <a
                    href={`tel:${t("footer.phone_value").replace(/[^+\d]/g, "")}`}
                    data-testid="footer-phone"
                    className="text-slate-100 hover:text-white"
                  >
                    {t("footer.phone_value")}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-[#2A6B7C]" />
                <div>
                  <div className="text-slate-500 text-xs uppercase tracking-widest">
                    {t("footer.email_label")}
                  </div>
                  <a
                    href={`mailto:${t("footer.email_value")}`}
                    data-testid="footer-email"
                    className="text-slate-100 hover:text-white"
                  >
                    {t("footer.email_value")}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-[#2A6B7C]" />
                <div>
                  <div className="text-slate-500 text-xs uppercase tracking-widest">
                    {t("footer.address_label")}
                  </div>
                  <div className="text-slate-100">
                    {t("footer.address_value")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">
              {t("footer.links_title")}
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to={localePath("/")}
                  data-testid="footer-link-home"
                  className="text-slate-200 hover:text-white"
                >
                  {t("footer.link_home")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/privacy-policy")}
                  data-testid="footer-link-privacy"
                  className="text-slate-200 hover:text-white"
                >
                  {t("footer.link_privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/cookie-policy")}
                  data-testid="footer-link-cookies"
                  className="text-slate-200 hover:text-white"
                >
                  {t("footer.link_cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath("/terms")}
                  data-testid="footer-link-terms"
                  className="text-slate-200 hover:text-white"
                >
                  {t("footer.link_terms")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">
              {t("footer.legal_title")}
            </div>
            <ul className="mt-4 space-y-2 text-xs text-slate-400 leading-relaxed">
              <li data-testid="footer-legal-1">{t("footer.company_line_1")}</li>
              <li data-testid="footer-legal-2">{t("footer.company_line_2")}</li>
              <li data-testid="footer-legal-3">{t("footer.company_line_3")}</li>
              <li data-testid="footer-legal-4">{t("footer.company_line_4")}</li>
              <li data-testid="footer-legal-5">{t("footer.company_line_5")}</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-[#1E293B] text-xs text-slate-500 leading-relaxed">
          <p data-testid="footer-disclaimer">{t("footer.disclaimer")}</p>
          <p className="mt-3" data-testid="footer-copyright">{t("footer.copyright")}</p>
        </div>
      </div>
    </footer>
  );
}
